import React, { useState, useRef, useEffect, useCallback } from 'react';
import Select, { components } from 'react-select';
import inputStyles from '../ManagementPages.module.css';
import searchStyles from '../../components/FilterManagementView.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector, useDispatch } from 'react-redux';
import { setFilterItem, setSelectedCharts, setSelectedReportTypes } from './Slices/PatientDetailFilterSlice';
import styles from './Patient.module.css';
import { SpecialtySelectedIcon } from '../../components/icons/Icons';
import 'react-datepicker/dist/react-datepicker.css';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import DateRangePicker from '../tasks/DateRangePicker';
import { formatMonthDay } from '../../utils/dateUtils';

import {
    startOfDay,
    endOfDay,
    isSameDay
  } from "date-fns";
import { faClipboardCheck, faUserDoctor } from '@fortawesome/free-solid-svg-icons';

const Option = ({ children, isSelected, innerProps, selectProps, data }) => {
    const isSelectAll = data.value === 'select-all';
    return (
        <div
            style={{
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                backgroundColor: isSelectAll ? '#f0f0f0' : 'white'
            }}
            {...innerProps}
        >
            <input
                type="checkbox"
                checked={isSelected}
                readOnly
                style={{ marginRight: '8px' }}
            />
            <span>{children}</span>
        </div>
    );
};

const MultiValue = () => null;

const ValueContainer = ({ children, getValue, ...props }) => {
  const selectedCount = getValue().length;
  const allOptionsCount = props.selectProps.options.length;

  return (
    <components.ValueContainer {...props}>
      {children[0]}
      {(selectedCount === allOptionsCount && allOptionsCount !== 0) ? 'All Selected' : 
       (selectedCount > 0 ? 
        `${selectedCount} Chart${selectedCount > 1 ? 's' : ''} Selected` 
       : 
       '')}
    </components.ValueContainer>
  );
};

const MenuList = ({ children, ...props }) => {
    const { selectProps } = props;
    const { options, value, onChange } = selectProps;
    const [selectAll, setSelectAll] = useState(false);

    const allOption = { value: 'select-all', label: 'Select All' };

    useEffect(() => {
        setSelectAll(value.length === options.filter(option => option.value !== 'select-all').length);
    }, [value, options]);

    const handleSelectAll = useCallback(() => {
        if (selectAll) {
            onChange([]);
        } else {
            onChange(options.filter(option => option.value !== 'select-all'));
        }
        setSelectAll(!selectAll);
    }, [selectAll, onChange, options]);

    return (
        <components.MenuList {...props}>
            <Option
                isSelected={selectAll}
                selectProps={selectProps}
                innerProps={{ onClick: handleSelectAll }}
                data={allOption}
            >
                {allOption.label}
            </Option>
            {children}
        </components.MenuList>
    );
};

const FilterPatientView = (props) => {
    const {
        isSearch = false,
        isDate = false,
        isTimePeriod = false,
        isNote = false,
        isReport = false,
        isCareTeam = false,
        timestamp,
        title
    } = props

    const dispatch = useDispatch();

    const dateOptions = [
        { label: "Ascending", value: "ascending" },
        { label: "Descending", value: "descending" },
    ];

    const noteTypes = [
        { label: "All", value: "" },
        { label: "Doctor Notes", value: "Doctor Notes" },
        { label: "Transcription", value: "Transcription" },
        { label: "EHR Notes", value: "EHR Note" },
    ];

    const reportTypes = [
       // { value: "all", label: "All" },
        { value: "new", label: "New" },
        { value: "open", label: "Open" },
        { value: "resolved", label: "Resolved" }
    ]

    const { dateOrder, timePeriod, noteType, startDate, endDate, chartOptions, selectedCharts, reportType,selectedReportTypes, readingInterval, averageGrouped } = useSelector((state) => state.patientDetailFilter);
    const filteredCharts = chartOptions.filter(chart => chart.showing);
    const [showDatePopover, setShowDatePopover] = useState(false);
    const customButtonRef = useRef(null);
    const selectRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    const safeSelectedFilterTypes = Array.isArray(selectedReportTypes) ? selectedReportTypes : [];

    const checkSelectedItem = (value) => {
        return safeSelectedFilterTypes.some(item => item.value === value);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (selectRef.current && !selectRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleDateOrderChange = (value) => {

        const today = new Date();
        const dayStart = startOfDay(today);
        let startDate, endDate;

        switch (value) {
            case 'Day':
                startDate = dayStart;
                endDate = endOfDay(today);
                break;
            case 'Week':
                endDate = endOfDay(today);
                let weekStart =  new Date(today);
                weekStart.setDate(today.getDate() - 7);
                startDate = startOfDay(weekStart);
                break;
            case 'Month':
                endDate = endOfDay(today);
                let monthStart =  new Date(today);
                monthStart.setDate(today.getDate() - 30);
                startDate = startOfDay(monthStart);
                break;
            default:
                startDate = dayStart;
                endDate = endOfDay(today);
                break;
        }

        startDate = startDate.toISOString();
        endDate = endDate.toISOString();
        dispatch(setFilterItem({ key: "timePeriod", value: value }));
        dispatch(setFilterItem({ key: "startDate", value: startDate }));
        dispatch(setFilterItem({ key: "endDate", value: endDate }));
    };

    const handleFilterChange = (item) => {
        if (item?.target) {
            dispatch(setFilterItem({ key: item?.target.id, value: item?.target.value }));
        }else{
            const isSelected = checkSelectedItem(item.value);
            const updatedFilterTypes = isSelected
              ? safeSelectedFilterTypes.filter(selectedItem => selectedItem?.value?.toLowerCase() !== item?.value?.toLowerCase())
              : [...safeSelectedFilterTypes, item];
          
            dispatch(setSelectedReportTypes(updatedFilterTypes));
        }
       
      };
      
    

    const vitalFilters = [
        { label: "Day", value: "Day" },
        { label: "Week", value: "Week" },
        { label: "Month", value: "Month" },
        { label: "Custom", value: "Custom" },
    ];

    const intervalOptions = [
        { label: "Day", value: "Day" },
        { label: "12 Hours", value: "12Hour" },
        { label: "6 Hours", value: "6Hour" },
    ];

    const intervalTooltip = (
        <Tooltip id="interval-tooltip" className={styles.IntervalTooltip}>
            The selected interval determines the time span used to calculate the average value. For example:
            <ul style={{ paddingLeft: '20px', margin: 0 }}>
            <li><strong>Day</strong>: Averages are computed over a 24-hour period.</li>
            <li><strong>12 Hours</strong>: Averages are computed for every half day.</li>
            <li><strong>6 Hours</strong>: Averages are computed for every 6-hour period.</li>
            </ul>
            The timestamps displayed on the graph represent the start time of each interval.
        </Tooltip>
    );

    const handleChartSelection = (selectedOptions) => {
        const selectedValues = selectedOptions.map(option => option.value);
        dispatch(setSelectedCharts(selectedValues));
    };
    const handleCustomDateClick = (event) => {
        event.stopPropagation();
        setShowDatePopover(true);
    };

     const handleDateRangeSave = (startDate, endDate) => {
        let start, end;
        if (startDate && endDate) {
            start = new Date(startDate);
            end = new Date(endDate);
        } else if (startDate) {
            start = new Date(startDate);
            end = new Date(startDate);
        } else if (endDate) {
            start = new Date(endDate);
            end = new Date(endDate);
        } else {
            return;
        }

        const formattedStartDate = start.toISOString();
        const formattedEndDate = end.toISOString();

        dispatch(setFilterItem({ key: "timePeriod", value: 'Custom' }));
        dispatch(setFilterItem({ key: "startDate", value: formattedStartDate }));
        dispatch(setFilterItem({ key: "endDate", value: formattedEndDate }));
    };

    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);

    return (
        <div style={{
            display: "flex",
            justifyContent: "space-between",
            // alignItems: "center",
            paddingLeft: "5px",
            paddingRight: "10px",
            flexWrap: "nowrap",
            width: "100%"
        }}>
            {isTimePeriod &&
                <div style={{
                    marginRight: "20px",
                    fontFamily: "Roboto",
                    fontSize: "16px",
                    fontWeight: 500,
                    lineHeight: "28px",
                    letterSpacing: "0.0015em",
                    textAlign: "left",
                    color: "#4F4F4F",
                    flexShrink: 0,
                    paddingLeft: "10px",
                    height: '38px',
                    display: 'flex',
                    alignItems: 'center'
                }}>
                    {(() => {
                        if (startDate && endDate) {
                            const start = new Date(startDate);
                            const end = new Date(endDate);
                            
        
                                const startTime = start.toLocaleString('en-US', { 
                                    hour: 'numeric', 
                                    minute: 'numeric' 
                                });
                                const endTime = end.toLocaleString('en-US', { 
                                    hour: 'numeric', 
                                    minute: 'numeric' 
                                });
                
                
                                if (formatMonthDay(start.toISOString(), userDateFormat) === formatMonthDay(end.toISOString(), userDateFormat) && startTime === '12:00 AM' && endTime === '11:59 PM') {
                                    return formatMonthDay(start.toISOString(), userDateFormat);
                                } else if (timePeriod === "Week" || timePeriod === "Month") {
                                    return `${formatMonthDay(start.toISOString(), userDateFormat)} - ${formatMonthDay(end.toISOString(), userDateFormat)}`;
                                } else {
                                    return `${formatMonthDay(start.toISOString(), userDateFormat)} ${startTime} - ${formatMonthDay(end.toISOString(), userDateFormat)} ${endTime}`;
                                }

                        }
                        return '';
                    })()}
                </div>
            }
            {title &&
                <span className={styles.NotesTitle}>{title}</span>
            }
            {timestamp &&
                <div style={{color: "#666", fontSize: "14px", display:"flex", alignItems:"center", height:"38px"}}>
                    Latest Recording: {timestamp}
                </div>
            }
            {isCareTeam && 
                <div style={{display:"flex", height:"38px", alignSelf:"start", alignItems:"center", columnGap:"5px"}}>
                    <FontAwesomeIcon icon={faUserDoctor} />
                    <span style={{fontSize:"11px", fontFamily:"Roboto", paddingRight:"10px"}}>Primary Care Physician </span>
                    <FontAwesomeIcon icon={faClipboardCheck} />
                    <span style={{fontSize:"11px", fontFamily:"Roboto"}}>Alert Authorized </span>
                </div>
            }
            <div style={{ flex: 1 }}></div>
            {isTimePeriod &&
                <div style={{display:"flex", flexDirection:"column", gap: '5px'}}>
                    <div className={styles.GraphPeriodButtons} style={{ marginRight: '10px' }}>
                        {vitalFilters.map((filter, index) => (
                            <div
                                key={index}
                                ref={filter.value === 'Custom' ? customButtonRef : null}
                                className={timePeriod.includes(filter.value) ? styles.TimePeriodSelected : ''}
                                onClick={(event) => {
                                    if (filter.value === 'Custom') {
                                        handleCustomDateClick(event);
                                    } else {
                                        handleDateOrderChange(filter.value);
                                        setShowDatePopover(false);
                                    }
                                }}
                            >
                                {timePeriod.includes(filter.value) && <SpecialtySelectedIcon />}
                                {filter.label}
                            </div>
                        ))}
                    </div>
                    {(averageGrouped) && 
                        <div style={{ display:"flex", columnGap:"10px", marginLeft:"5px" }}>
                            <div className="caption">{"Interval"}</div>
                            <OverlayTrigger placement="right" overlay={intervalTooltip}>
                                <FontAwesomeIcon icon="fa-solid fa-circle-info" style={{color: '#686869'}}/>
                            </OverlayTrigger>
                            <div className="caption">{": "}</div>
                            {intervalOptions.map((interval) => (
                                <div key={interval.value} style={{display:"flex", alignItems:"center"}}>
                                    <input
                                        id="unitType" 
                                        type="radio"
                                        value={interval.value}
                                        checked={readingInterval === interval.value}
                                        onChange={() => {
                                            dispatch(setFilterItem({ key: "readingInterval", value: interval.value }));
                                        }}
                                    /> 
                                    <span className="caption" style={{paddingLeft: "5px"}}>{interval.label}</span>
                                </div>
                            ))}
                        </div>
                    }
                </div>
            }
            {isTimePeriod &&
                <div style={{ position: "relative", width: "300px" }} ref={selectRef}>
                    <Select
                        isMulti
                        options={filteredCharts.map(chart => ({ value: chart.value, label: chart.label }))}
                        value={selectedCharts?.map(value => ({ value, label: filteredCharts.find(c => c.value === value)?.label })) || []}
                        onChange={handleChartSelection}
                        onMenuOpen={() => setIsOpen(true)}
                        onMenuClose={() => setIsOpen(false)}
                        menuIsOpen={isOpen}
                        placeholder="Select Charts"
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        components={{ Option, MultiValue, ValueContainer, MenuList }}
                        styles={{
                            option: (provided, state) => ({
                                ...provided,
                                backgroundColor: state.isFocused ? '#f0f0f0' : 'white',
                                color: 'black',
                            }),
                            multiValue: (provided) => ({
                                ...provided,
                                backgroundColor: '#e0e0e0',
                            }),
                            multiValueLabel: (provided) => ({
                                ...provided,
                                color: 'black',
                            }),
                            multiValueRemove: (provided) => ({
                                ...provided,
                                color: 'black',
                                ':hover': {
                                    backgroundColor: '#c0c0c0',
                                    color: 'black',
                                },
                            }),
                            valueContainer: (provided) => ({
                                ...provided,
                                display: 'flex',
                                flexWrap: 'nowrap',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }),
                            placeholder: (provided) => ({
                                ...provided,
                                display: selectedCharts?.length ? 'none' : 'block',
                            }),
                        }}
                    />
                </div>
            }
            <DateRangePicker 
                onSave={handleDateRangeSave}
                onClose={() => setShowDatePopover(false)}
                mode={"modal"}
                showModal={showDatePopover}
                timeSelect={true}
            />
            <div style={{ display: "flex", columnGap: "10px", flexWrap: "wrap" }}>
                {isSearch &&
                    <div style={{ display: "flex", alignItems: "center", position: "relative" }}>
                        <div style={{ position: "absolute", paddingLeft: "10px" }}>
                            <FontAwesomeIcon icon="fa-solid fa-magnifying-glass" size="xs" style={{ color: "D9D9D9" }} />
                        </div>
                        <input
                            id="searchText"
                            type="text"
                            placeholder="Search Item"
                            className={searchStyles.SearchInput}
                            onChange={handleFilterChange}
                        />
                    </div>
                }
                {isDate && (
                    <div style={{ position: "relative" }}>
                        <span className="floating-label">Date</span>
                        <select
                            id="dateOrder"
                            style={{ width: "140px" }}
                            className={inputStyles["styled-input-select"]}
                            onChange={handleFilterChange}
                            value={dateOrder}
                        >
                            {dateOptions.map((date) => (
                                <option key={date.value} value={date.value}>
                                    {date.label}
                                </option>
                            ))}
                        </select>
                    </div>)
                }
                {isNote && (
                    <div style={{ position: "relative" }}>
                        <span className="floating-label">Note Type</span>
                        <select
                            id="noteType"
                            style={{ width: "140px" }}
                            className={inputStyles["styled-input-select"]}
                            onChange={handleFilterChange}
                            value={noteType}
                        >
                            {noteTypes.map((date) => (
                                <option key={date.label} value={date.value}>
                                    {date.label}
                                </option>
                            ))}
                        </select>
                    </div>)
                }
                {isReport && (
                    <div style={{ position: "relative" }}>
                        <MultiSelectDropdown 
                            inputWidth='25vh'
                            options={reportTypes}
                            label='label'
                            id="reportType"
                            checkedKey='value'
                            checkSelectedItem={checkSelectedItem}
                            handleChange={handleFilterChange}
                            selectedItems={safeSelectedFilterTypes}
                            name='Report Type'
                        />
                    </div>)
                }
            </div>
        </div>
    );
};

export default FilterPatientView;
